import { useState, useEffect, useCallback } from 'react';
import { X, Zap } from 'lucide-react';
import { useJob, useJobApplications, useUpdateJobCriteria } from '../hooks/useApi';

type Weight = 'high' | 'medium' | 'low';

interface Criterion {
  id?: number;
  name: string;
  weight: Weight;
  enabled: boolean;
}

interface CriteriaSetupModalProps {
  jobId: number;
  jobTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

const WEIGHT_CYCLE: Weight[] = ['high', 'medium', 'low'];
const WEIGHT_COLORS: Record<Weight, string> = {
  high: 'bg-amber-500 text-white',
  medium: 'bg-stone-500 dark:bg-stone-600 text-white',
  low: 'bg-stone-200 dark:bg-gray-600 text-stone-600 dark:text-stone-300',
};
const WEIGHT_LABELS: Record<Weight, string> = { high: 'High', medium: 'Medium', low: 'Low' };

function cycleWeight(w: Weight): Weight {
  const idx = WEIGHT_CYCLE.indexOf(w);
  return WEIGHT_CYCLE[(idx + 1) % WEIGHT_CYCLE.length];
}

function criteriaToText(criteria: Criterion[]): string {
  return criteria
    .filter((c) => c.enabled)
    .map((c) => `[${c.weight.toUpperCase()}] ${c.name}`)
    .join('\n');
}

export function CriteriaSetupModal({ jobId, jobTitle, isOpen, onClose }: CriteriaSetupModalProps) {
  const { data: job } = useJob(jobId);
  const { data: appsData } = useJobApplications(jobId, 1, 1, 'createdAt', 'desc');
  const { mutateAsync: saveAsync, isPending: saving } = useUpdateJobCriteria();
  const [criteria, setCriteria] = useState<Criterion[]>([]);

  // Load criteria from job when available
  useEffect(() => {
    const jobCriteria = (job as any)?.criteria as Criterion[] | undefined;
    if (jobCriteria && jobCriteria.length > 0) {
      setCriteria(jobCriteria);
    }
  }, [job]);

  // Focus trap: close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const candidateCount = (appsData as any)?.total ?? 0;

  function toggleWeight(index: number) {
    setCriteria((prev) =>
      prev.map((c, i) => (i === index ? { ...c, weight: cycleWeight(c.weight) } : c))
    );
  }

  function toggleEnabled(index: number) {
    setCriteria((prev) =>
      prev.map((c, i) => (i === index ? { ...c, enabled: !c.enabled } : c))
    );
  }

  async function handleSave() {
    const criteriaText = criteriaToText(criteria);
    await saveAsync({ jobId, criteriaText, changeReason: 'Updated via setup modal' });
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 dark:bg-black/60 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="criteria-modal-title"
          className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col border border-stone-200 dark:border-gray-700"
        >
          {/* Header */}
          <div className="flex items-start gap-3 p-5 border-b border-stone-100 dark:border-gray-800">
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
              <Zap size={14} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h2
                id="criteria-modal-title"
                className="font-semibold text-stone-800 dark:text-stone-100"
              >
                Set up AI review criteria
              </h2>
              <p className="text-sm text-stone-500 dark:text-stone-400 truncate">{jobTitle}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 rounded"
            >
              <X size={16} />
            </button>
          </div>

          {/* Arbie's intro */}
          <div className="mx-5 mt-4 bg-stone-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-stone-700 dark:text-stone-200">
            I drafted these from the job description. Tap a weight to cycle it, toggle criteria
            off, then save — I&apos;ll score all{' '}
            {candidateCount > 0 ? candidateCount : ''} candidates.
          </div>

          {/* Criteria list */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
            <p className="text-[10px] font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-wider mb-2">
              Scoring Criteria
            </p>
            {criteria.length === 0 ? (
              <p className="text-sm text-stone-400 dark:text-stone-500 text-center py-8">
                No criteria defined yet.
              </p>
            ) : (
              criteria.map((c, i) => (
                <div
                  key={c.id ?? i}
                  className="flex items-center gap-3 bg-stone-50 dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-lg px-3 py-2.5"
                >
                  <span className="flex-1 text-sm text-stone-800 dark:text-stone-100">
                    {c.name}
                  </span>
                  <button
                    onClick={() => toggleWeight(i)}
                    className={`text-xs font-medium px-2 py-0.5 rounded transition-colors ${WEIGHT_COLORS[c.weight]}`}
                  >
                    {WEIGHT_LABELS[c.weight]}
                  </button>
                  {/* Toggle switch */}
                  <button
                    role="switch"
                    aria-checked={c.enabled}
                    onClick={() => toggleEnabled(i)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                      c.enabled ? 'bg-amber-500' : 'bg-stone-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
                        c.enabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-stone-100 dark:border-gray-800 flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-gray-800 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || criteria.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-50 transition-colors"
            >
              <Zap size={14} />
              {candidateCount > 0
                ? `Save & review ${candidateCount} candidates`
                : 'Save criteria'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
